import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useI18n } from '../i18n'

function Item({ to, onClick, icon, children }: { to: string; onClick: () => void; icon: JSX.Element; children: any }) {
  return (
    <Link to={to} onClick={onClick} className="inline-flex items-center gap-2 btn-secondary !bg-gray-100 dark:!bg-gray-700 !text-gray-800 dark:!text-gray-100 hover:!bg-gray-200 dark:hover:!bg-gray-600 w-full">
      {icon}
      <span>{children}</span>
    </Link>
  )
}

export default function MenuDropdown() {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as any)) setOpen(false) }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('click', onDoc)
    document.addEventListener('keydown', onKey)
    return () => { document.removeEventListener('click', onDoc); document.removeEventListener('keydown', onKey) }
  }, [])
  const close = () => setOpen(false)
  return (
    <div className="relative" ref={ref}>
      <button className="btn-secondary" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(v => !v)}>
        {/* hamburger icon */}
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M4 6h16v2H4V6Zm0 5h16v2H4v-2Zm0 5h16v2H4v-2Z"/></svg>
        {t('nav.menu') || 'Menu'}
      </button>
      {open && (
        <div
          role="menu"
          className="absolute mt-2 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded shadow text-sm p-2 z-50 min-w-[280px]"
          onKeyDown={(e) => {
            if (e.key !== 'Tab') return
            const container = (e.currentTarget as HTMLElement)
            const focusables = Array.from(container.querySelectorAll<HTMLElement>('a,button,[tabindex]:not([tabindex="-1"])')).filter(el => !el.hasAttribute('disabled'))
            if (!focusables.length) return
            const first = focusables[0]
            const last = focusables[focusables.length - 1]
            const active = document.activeElement as HTMLElement
            if (e.shiftKey) {
              if (active === first || !container.contains(active)) { last.focus(); e.preventDefault() }
            } else {
              if (active === last) { first.focus(); e.preventDefault() }
            }
          }}
        >
          <div className="grid grid-cols-2 gap-2">
            <Item to="/tenants" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 4h18v2H3V4Zm2 4h14v2H5V8Zm-2 4h18v2H3v-2Zm2 4h10v2H5v-2Z"/></svg>}>{t('nav.tenants')}</Item>
            <Item to="/endpoints" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M4 5h16v6H4V5Zm0 8h10v6H4v-6Zm12 0h4v6h-4v-6Z"/></svg>}>{t('nav.endpoints')}</Item>
            <Item to="/policies" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M5 4h14v2H5V4Zm0 4h14v2H5V8Zm0 4h10v2H5v-2Zm0 4h8v2H5v-2Z"/></svg>}>{t('nav.policies')}</Item>
            <Item to="/schedules" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M7 3h2v3h6V3h2v3h3v14H4V6h3V3Zm12 7H5v7h14v-7Z"/></svg>}>{t('nav.schedules')}</Item>
            <Item to="/repos" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M4 4h16v10H4V4Zm0 12h16v2H4v-2Z"/></svg>}>{t('nav.repos')}</Item>
            <Item to="/jobs" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M4 7h16v11H4V7Zm3-3h10v2H7V4Z"/></svg>}>{t('nav.jobs')}</Item>
            <Item to="/restore" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M5 2h14v2H5V2Zm0 4h14v2H5V6Zm0 6h10v2H5v-2Zm12.59-.59L20 12.83l-2.41 2.42L16 14.66l1.59-1.83L16 11l1.59-1.59Z"/></svg>}>{t('nav.restore')}</Item>
            <Item to="/agents" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 5h18v2H3V5Zm2 4h14v2H5V9Zm-2 4h18v2H3v-2Zm2 4h10v2H5v-2Z"/></svg>}>Agents</Item>
            <Item to="/rbac" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-3.33 0-6 1.34-6 3v3h12v-3c0-1.66-2.67-3-6-3Z"/></svg>}>{t('nav.rbac')}</Item>
            <Item to="/health" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M3 13h5l2-4 4 8 2-4h5v2h-4l-2 4-4-8-2 4H3v-2Z"/></svg>}>{t('nav.health')}</Item>
            <Item to="/settings" onClick={close} icon={<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 8a4 4 0 1 0 4 4 4 4 0 0 0-4-4Zm8.94 3h-2.11a7 7 0 0 0-.48-1.15l1.49-1.49-1.41-1.41-1.49 1.49A7 7 0 0 0 15 5.17V3.06h-2v2.11a7 7 0 0 0-1.15.48L10.36 4.16 8.95 5.57l1.49 1.49A7 7 0 0 0 9.17 9H7.06v2h2.11a7 7 0 0 0 .48 1.15l-1.49 1.49 1.41 1.41 1.49-1.49c.37.2.76.36 1.15.48v2.11h2v-2.11c.4-.12.78-.28 1.15-.48l1.49 1.49 1.41-1.41-1.49-1.49c.2-.37.36-.76.48-1.15h2.11v-2Z"/></svg>}>{t('nav.settings') || 'Settings'}</Item>
          </div>
        </div>
      )}
    </div>
  )
}
